<template>
  <v-dialog v-model="dialog" max-width="820px" persistent scrollable>
    <v-card>
      <v-card-title class="primary--text">
        {{ isEdit ? 'Editar Torneo' : 'Crear Nuevo Torneo' }}
      </v-card-title>
      <v-divider></v-divider>

      <v-stepper v-model="step" class="elevation-0 stepper-body" non-linear>
        <v-stepper-header class="elevation-0">
          <template v-for="(s, i) in steps">
            <v-stepper-step
              :key="'h' + s.id"
              :step="s.id"
              :complete="step > s.id"
              :editable="maxStep >= s.id"
              edit-icon="mdi-check"
            >
              {{ s.title }}
            </v-stepper-step>
            <v-divider v-if="i < steps.length - 1" :key="'d' + s.id"></v-divider>
          </template>
        </v-stepper-header>

        <v-stepper-items>
          <!-- Paso 1: Información -->
          <v-stepper-content step="1">
            <v-form ref="form1" @submit.prevent="next(1)">
              <v-text-field
                v-model="form.name"
                label="Nombre del torneo"
                outlined
                dense
                :rules="[rules.required]"
                class="mb-2"
              ></v-text-field>

              <v-textarea
                v-model="form.description"
                label="Descripción"
                outlined
                dense
                rows="3"
                class="mb-2"
              ></v-textarea>

              <v-select
                v-model="form.tournament_type"
                :items="tournamentTypes"
                item-text="text"
                item-value="value"
                label="Tipo de torneo"
                outlined
                dense
                hint="Los torneos de niños permiten registrar hijos bajo un padre o tutor"
                persistent-hint
                class="mb-4"
              ></v-select>

              <!-- Imagen (opcional) -->
              <div>
                <label class="font-weight-bold text-caption d-block mb-2">
                  Imagen del torneo <span class="grey--text">(opcional)</span>
                </label>
                <div v-if="imagePreview" class="image-preview mb-3">
                  <img :src="imagePreview" :alt="form.name || 'Imagen del torneo'">
                  <v-btn small text color="error" class="mt-1" @click="clearImage">
                    <v-icon small left>mdi-delete</v-icon>
                    Quitar imagen
                  </v-btn>
                </div>
                <v-file-input
                  v-model="imageFile"
                  accept="image/png,image/jpeg,image/webp"
                  :label="imagePreview ? 'Reemplazar imagen' : 'Seleccionar imagen'"
                  outlined
                  dense
                  prepend-icon=""
                  prepend-inner-icon="mdi-image"
                  :rules="[rules.imageSize]"
                  show-size
                  hint="JPG, PNG o WEBP, máximo 5 MB"
                  persistent-hint
                  @change="onImageSelected"
                ></v-file-input>
              </div>
            </v-form>
          </v-stepper-content>

          <!-- Paso 2: Fechas y juego -->
          <v-stepper-content step="2">
            <v-form ref="form2" @submit.prevent="next(2)">
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.start_date"
                    label="Fecha de inicio"
                    type="date"
                    outlined
                    dense
                    :rules="[rules.required]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.end_date"
                    label="Fecha de fin"
                    type="date"
                    outlined
                    dense
                    :rules="[rules.required, rules.endAfterStart]"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.category"
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    label="Categoría"
                    outlined
                    dense
                    :rules="[rules.required]"
                  >
                    <template v-slot:item="{ item }">
                      <div>
                        <strong>{{ item.name }}</strong>
                        <div v-if="item.handicapMin !== null" class="text-caption grey--text">
                          Índice: {{ item.handicapMin }} a {{ item.handicapMax }}
                        </div>
                        <div v-else class="text-caption grey--text">Sin restricción de handicap</div>
                      </div>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.game_system"
                    :items="gameSystems"
                    item-text="name"
                    item-value="id"
                    label="Sistema de juego"
                    outlined
                    dense
                    :rules="[rules.required]"
                  ></v-select>
                </v-col>
              </v-row>

              <v-checkbox
                v-model="form.handicap_free"
                label="Handicap libre (sin restricción)"
                dense
                class="mt-0"
              ></v-checkbox>

              <v-alert v-if="!form.handicap_free" type="info" dense text class="mb-0">
                Rango según la categoría:
                <strong>{{ form.handicap_min !== null ? form.handicap_min : '—' }}</strong> a
                <strong>{{ form.handicap_max !== null ? form.handicap_max : '—' }}</strong>
                <span v-if="form.handicap_min === null"> (sin restricción para esta categoría)</span>
              </v-alert>
              <v-alert v-else type="info" dense text class="mb-0">
                Jugadores de cualquier handicap podrán participar.
              </v-alert>
            </v-form>
          </v-stepper-content>

          <!-- Paso 3: Salidas y horarios -->
          <v-stepper-content step="3">
            <v-form ref="form3" @submit.prevent="next(3)">
              <v-select
                v-model="form.courses"
                :items="courts"
                item-text="name"
                item-value="id"
                label="Salidas"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                :rules="[rules.minOneCourse]"
                :loading="loadingCourts"
                class="mb-2"
              >
                <template v-slot:item="{ item }">
                  <div>
                    <strong>{{ item.name }}</strong>
                    <div class="text-caption grey--text">
                      {{ item.opening_time }} - {{ item.closing_time }} (Intervalo: {{ item.time_interval }} min)
                    </div>
                  </div>
                </template>
              </v-select>

              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.start_time"
                    label="Hora de inicio"
                    type="time"
                    outlined
                    dense
                    :rules="[rules.required]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.end_time"
                    label="Hora de fin"
                    type="time"
                    outlined
                    dense
                    :rules="[rules.required, rules.endTimeAfterStart]"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" sm="6">
                  <v-checkbox
                    v-model="form.starting_shotgun"
                    label="Escopetazo inicial"
                    dense
                    class="mt-0"
                  ></v-checkbox>
                </v-col>
                <v-col v-if="form.starting_shotgun" cols="12" sm="6">
                  <v-text-field
                    v-model="form.shotgun_time"
                    label="Hora del escopetazo"
                    type="time"
                    outlined
                    dense
                    :rules="[rules.required]"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-stepper-content>

          <!-- Paso 4: Inscripciones -->
          <v-stepper-content step="4">
            <v-form ref="form4" @submit.prevent="next(4)">
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="form.max_players"
                    label="Máx. participantes"
                    type="number"
                    min="1"
                    outlined
                    dense
                    :rules="[rules.required, rules.minValue]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="form.max_guests_per_member"
                    :label="form.tournament_type === 'kids' ? 'Máx. niños por padre' : 'Máx. invitados por jugador'"
                    type="number"
                    min="0"
                    max="10"
                    outlined
                    dense
                    :rules="[rules.nonNegative]"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-select
                v-model="form.status"
                :items="statusOptions"
                item-text="text"
                item-value="value"
                label="Estado"
                outlined
                dense
                hint="Solo se aceptan registros cuando está en «Abierto inscripciones»"
                persistent-hint
                :rules="[rules.required]"
                class="mb-4"
              ></v-select>

              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.registration_open_at"
                    label="Apertura de inscripciones (opcional)"
                    type="datetime-local"
                    outlined
                    dense
                    clearable
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.registration_close_at"
                    label="Cierre de inscripciones (opcional)"
                    type="datetime-local"
                    outlined
                    dense
                    clearable
                    :rules="[rules.closeAfterOpen]"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-checkbox
                    v-model="form.allow_children"
                    :disabled="form.tournament_type !== 'kids'"
                    label="Permitir niños"
                    dense
                    hide-details
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-checkbox
                    v-model="form.send_whatsapp"
                    label="Enviar WhatsApp al registrarse"
                    dense
                    hide-details
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-checkbox
                    v-model="form.send_email"
                    label="Enviar correo al registrarse"
                    dense
                    hide-details
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-form>
          </v-stepper-content>

          <!-- Paso 5: Resumen -->
          <v-stepper-content step="5">
            <v-simple-table dense class="mb-4">
              <tbody>
                <tr v-for="row in summaryRows" :key="row.label">
                  <td class="font-weight-bold" style="width: 38%">{{ row.label }}</td>
                  <td>{{ row.value }}</td>
                </tr>
              </tbody>
            </v-simple-table>

            <div v-if="imagePreview" class="image-preview mb-4">
              <img :src="imagePreview" alt="Imagen del torneo">
            </div>

            <div v-if="registrationLink" class="mb-2">
              <label class="font-weight-bold text-caption d-block mb-2">Enlace público de registro</label>
              <v-row dense align="center">
                <v-col cols="12" sm="4" class="text-center">
                  <img :src="qrCodeUrl" alt="QR de registro" class="qr">
                </v-col>
                <v-col cols="12" sm="8">
                  <v-text-field
                    :value="registrationLink"
                    outlined
                    dense
                    readonly
                    hide-details
                    append-icon="mdi-content-copy"
                    @click:append="copyToClipboard"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
            <v-alert v-else type="info" dense text class="mb-0">
              El enlace y el código QR de registro se generan al guardar el torneo.
            </v-alert>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <v-divider></v-divider>
      <v-card-actions>
        <v-btn text :disabled="saving" @click="$emit('close')">Cancelar</v-btn>
        <v-spacer></v-spacer>
        <v-btn v-if="step > 1" text :disabled="saving" @click="step -= 1">
          <v-icon small left>mdi-chevron-left</v-icon>
          Atrás
        </v-btn>
        <v-btn v-if="step < steps.length" color="primary" @click="next(step)">
          Siguiente
          <v-icon small right>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn v-else color="primary" :loading="saving" @click="handleSubmit">
          {{ isEdit ? 'Actualizar' : 'Crear torneo' }}
        </v-btn>
      </v-card-actions>

      <v-snackbar v-model="snackbar.show" color="error" top>{{ snackbar.text }}</v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { GOLF_CATEGORIES, GAME_SYSTEMS, TOURNAMENT_STATUS, TOURNAMENT_TYPES } from '../constants/tournamentConfig'

const API_BASE_URL = 'https://clubtest.intrare.app'
const MAX_IMAGE_BYTES = 5 * 1024 * 1024

export default {
  name: 'CreateTournamentDialog',
  props: {
    tournament: {
      type: Object,
      default: null
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialog: true,
      step: 1,
      maxStep: 1,
      steps: [
        { id: 1, title: 'Información' },
        { id: 2, title: 'Fechas y juego' },
        { id: 3, title: 'Salidas' },
        { id: 4, title: 'Inscripciones' },
        { id: 5, title: 'Resumen' }
      ],
      loadingCourts: false,
      courts: [],
      imageFile: null,
      imagePreview: null,
      removeImage: false,
      qrCodeUrl: null,
      registrationLink: '',
      snackbar: { show: false, text: '' },
      form: {
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        courses: [],
        category: 'championship',
        game_system: 'stroke_play',
        max_players: 20,
        handicap_min: null,
        handicap_max: null,
        handicap_free: false,
        start_time: '',
        end_time: '',
        starting_shotgun: false,
        shotgun_time: '',
        status: 1,
        tournament_type: 'adult',
        registration_open_at: '',
        registration_close_at: '',
        max_guests_per_member: 3,
        allow_children: false,
        send_whatsapp: true,
        send_email: true
      },
      categories: GOLF_CATEGORIES,
      gameSystems: GAME_SYSTEMS,
      statusOptions: TOURNAMENT_STATUS,
      tournamentTypes: TOURNAMENT_TYPES
    }
  },
  computed: {
    isEdit () {
      return !!this.tournament
    },
    rules () {
      return {
        required: (v) => (v !== null && v !== undefined && String(v).trim() !== '') || 'Campo requerido',
        minValue: (v) => v > 0 || 'Debe ser mayor a 0',
        nonNegative: (v) => (v !== '' && v !== null && v >= 0) || 'Debe ser 0 o mayor',
        minOneCourse: (v) => (Array.isArray(v) && v.length > 0) || 'Selecciona al menos una salida',
        endAfterStart: (v) => !v || !this.form.start_date || v >= this.form.start_date || 'No puede ser anterior al inicio',
        endTimeAfterStart: (v) => !v || !this.form.start_time || v > this.form.start_time || 'Debe ser posterior a la hora de inicio',
        closeAfterOpen: (v) => !v || !this.form.registration_open_at || v > this.form.registration_open_at || 'Debe ser posterior a la apertura',
        imageSize: (file) => !file || file.size <= MAX_IMAGE_BYTES || 'La imagen no debe pesar más de 5 MB'
      }
    },
    selectedCoursesNames () {
      return this.form.courses
        .map(id => {
          const court = this.courts.find(c => c.id === id)
          return court ? court.name : id
        })
        .join(', ')
    },
    summaryRows () {
      const f = this.form
      const category = this.categories.find(c => c.id === f.category)
      const system = this.gameSystems.find(s => s.id === f.game_system)
      const status = this.statusOptions.find(s => s.value === f.status)
      const type = this.tournamentTypes.find(t => t.value === f.tournament_type)
      const handicap = f.handicap_free
        ? 'Libre'
        : (f.handicap_min !== null ? `${f.handicap_min} a ${f.handicap_max}` : 'Sin restricción')
      return [
        { label: 'Nombre', value: f.name || '—' },
        { label: 'Tipo', value: type ? type.text : '—' },
        { label: 'Fechas', value: `${f.start_date || '—'} al ${f.end_date || '—'}` },
        { label: 'Categoría', value: category ? category.name : '—' },
        { label: 'Sistema de juego', value: system ? system.name : '—' },
        { label: 'Handicap', value: handicap },
        { label: 'Salidas', value: this.selectedCoursesNames || '—' },
        { label: 'Horario', value: `${f.start_time || '—'} a ${f.end_time || '—'}${f.starting_shotgun ? ` (escopetazo ${f.shotgun_time})` : ''}` },
        { label: 'Cupo', value: `${f.max_players} participantes · ${f.max_guests_per_member} ${f.tournament_type === 'kids' ? 'niños' : 'invitados'} por registro` },
        { label: 'Estado', value: status ? status.text : '—' },
        { label: 'Ventana de inscripción', value: this.windowText },
        { label: 'Notificaciones', value: this.notificationsText },
        { label: 'Imagen', value: this.imagePreview ? 'Sí' : 'Sin imagen' }
      ]
    },
    windowText () {
      const f = this.form
      if (!f.registration_open_at && !f.registration_close_at) return 'Sin restricción de fechas'
      const fmt = v => (v ? v.replace('T', ' ') : '—')
      return `${fmt(f.registration_open_at)} → ${fmt(f.registration_close_at)}`
    },
    notificationsText () {
      const channels = []
      if (this.form.send_whatsapp) channels.push('WhatsApp')
      if (this.form.send_email) channels.push('Correo')
      return channels.length ? channels.join(' y ') : 'Ninguna'
    }
  },
  watch: {
    'form.category' () {
      this.updateHandicapRange()
    },
    'form.handicap_free' () {
      if (this.form.handicap_free) {
        this.form.handicap_min = null
        this.form.handicap_max = null
      } else {
        this.updateHandicapRange()
      }
    },
    'form.tournament_type' (type) {
      if (type !== 'kids') this.form.allow_children = false
    },
    step (value) {
      if (value > this.maxStep) this.maxStep = value
    }
  },
  mounted () {
    this.loadCourts()
    if (this.tournament) {
      this.form = {
        ...this.form,
        ...this.tournament,
        courses: this.tournament.courses || [],
        start_time: this.trimSeconds(this.tournament.start_time),
        end_time: this.trimSeconds(this.tournament.end_time),
        starting_shotgun: this.tournament.starting_shotgun || false,
        shotgun_time: this.trimSeconds(this.tournament.shotgun_time),
        category: this.tournament.category || 'championship',
        game_system: this.tournament.game_system || 'stroke_play',
        handicap_free: this.tournament.handicap_free || false,
        handicap_min: this.tournament.handicap_min,
        handicap_max: this.tournament.handicap_max,
        tournament_type: this.tournament.tournament_type || 'adult',
        registration_open_at: this.toLocalInput(this.tournament.registration_open_at),
        registration_close_at: this.toLocalInput(this.tournament.registration_close_at),
        max_guests_per_member: this.tournament.max_guests_per_member != null ? this.tournament.max_guests_per_member : 3,
        allow_children: !!this.tournament.allow_children,
        send_whatsapp: this.tournament.send_whatsapp !== false,
        send_email: this.tournament.send_email !== false
      }
      this.imagePreview = this.tournament.image || null
      this.maxStep = this.steps.length // al editar se puede saltar entre pasos
      this.setRegistrationLink(this.tournament.public_registration_url)
    } else {
      this.updateHandicapRange()
    }
  },
  beforeDestroy () {
    this.revokePreview()
  },
  methods: {
    async loadCourts () {
      this.loadingCourts = true
      try {
        const idHousingUnit = store.getters.getProfile?.idHousingUnit || 'a58d8679-e2ae-4055-a243-c63d438f19eb'
        const response = await axios({
          method: 'get',
          url: `${API_BASE_URL}/golf-courses/?housing_unit=${idHousingUnit}`,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${store.getters.getToken}`
          }
        })
        this.courts = response.data || []
      } catch (error) {
        console.error('Error loading courts:', error)
        this.showError('No se pudieron cargar las salidas disponibles')
      } finally {
        this.loadingCourts = false
      }
    },
    // ---- Imagen ----
    onImageSelected (file) {
      this.revokePreview()
      if (!file) {
        this.imagePreview = this.removeImage ? null : (this.tournament && this.tournament.image) || null
        return
      }
      if (!file.type.startsWith('image/') || file.size > MAX_IMAGE_BYTES) {
        this.imageFile = null
        this.showError('Selecciona una imagen JPG, PNG o WEBP de máximo 5 MB')
        return
      }
      this.removeImage = false
      this.imagePreview = URL.createObjectURL(file)
    },
    clearImage () {
      this.revokePreview()
      this.imageFile = null
      this.imagePreview = null
      // Si ya tenía una imagen guardada, se pide quitarla al guardar
      this.removeImage = !!(this.tournament && this.tournament.image)
    },
    revokePreview () {
      if (this.imagePreview && this.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.imagePreview)
      }
    },
    // ---- Utilidades ----
    setRegistrationLink (url) {
      this.registrationLink = url || ''
      this.qrCodeUrl = url
        ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`
        : null
    },
    copyToClipboard () {
      navigator.clipboard.writeText(this.registrationLink)
    },
    trimSeconds (time) {
      return time ? String(time).slice(0, 5) : ''
    },
    // ISO del API -> valor de <input type="datetime-local"> en hora local
    toLocalInput (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    updateHandicapRange () {
      const category = this.categories.find(cat => cat.id === this.form.category)
      if (category && !this.form.handicap_free) {
        this.form.handicap_min = category.handicapMin
        this.form.handicap_max = category.handicapMax
      }
    },
    showError (text) {
      this.snackbar = { show: true, text }
    },
    // ---- Navegación por pasos ----
    validateStep (n) {
      const formRef = this.$refs['form' + n]
      return formRef ? formRef.validate() : true
    },
    next (n) {
      if (!this.validateStep(n)) return
      if (n === 3 && this.form.starting_shotgun && !this.form.shotgun_time) {
        this.showError('Indica la hora del escopetazo')
        return
      }
      this.step = Math.min(n + 1, this.steps.length)
    },
    handleSubmit () {
      // Valida todos los pasos y regresa al primero con errores
      for (let n = 1; n <= 4; n++) {
        if (!this.validateStep(n)) {
          this.step = n
          return
        }
      }
      if (this.form.starting_shotgun && !this.form.shotgun_time) {
        this.step = 3
        this.showError('Indica la hora del escopetazo')
        return
      }

      const f = this.form
      const payload = {
        name: f.name,
        description: f.description,
        start_date: f.start_date,
        end_date: f.end_date,
        courses: f.courses,
        max_players: f.max_players,
        status: f.status,
        category: f.category,
        game_system: f.game_system,
        handicap_free: f.handicap_free,
        handicap_min: f.handicap_min,
        handicap_max: f.handicap_max,
        start_time: f.start_time,
        end_time: f.end_time,
        starting_shotgun: f.starting_shotgun,
        shotgun_time: f.starting_shotgun ? f.shotgun_time : null,
        tournament_type: f.tournament_type,
        max_guests_per_member: f.max_guests_per_member,
        allow_children: f.tournament_type === 'kids' && f.allow_children,
        send_whatsapp: f.send_whatsapp,
        send_email: f.send_email,
        registration_open_at: f.registration_open_at ? new Date(f.registration_open_at).toISOString() : null,
        registration_close_at: f.registration_close_at ? new Date(f.registration_close_at).toISOString() : null
      }

      // La imagen se sube aparte (multipart) una vez guardado el torneo
      const extras = { imageFile: this.imageFile || null, removeImage: this.removeImage }
      this.$emit(this.isEdit ? 'update' : 'create', payload, extras)
    }
  }
}
</script>

<style scoped>
.stepper-body {
  box-shadow: none;
}

.image-preview img {
  display: block;
  max-width: 100%;
  max-height: 200px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.qr {
  width: 140px;
  height: 140px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 4px;
}
</style>
